import React, { useState } from 'react';
import SEO from './SEO';

const JsonFormatter: React.FC = () => {
  const [inputJson, setInputJson] = useState('');
  const [outputJson, setOutputJson] = useState('');
  const [error, setError] = useState('');
  const [isValid, setIsValid] = useState<boolean | null>(null);

  const formatJson = (indent: number = 2) => {
    if (!inputJson.trim()) {
      setError('请输入JSON数据');
      return;
    }

    try {
      const parsed = JSON.parse(inputJson);
      const formatted = JSON.stringify(parsed, null, indent);
      setOutputJson(formatted);
      setError('');
      setIsValid(true);
    } catch (err) {
      setError(`JSON格式错误: ${(err as Error).message}`);
      setOutputJson('');
      setIsValid(false);
    }
  };

  const minifyJson = () => {
    if (!inputJson.trim()) {
      setError('请输入JSON数据');
      return;
    }

    try {
      const parsed = JSON.parse(inputJson);
      const minified = JSON.stringify(parsed);
      setOutputJson(minified);
      setError('');
      setIsValid(true);
    } catch (err) {
      setError(`JSON格式错误: ${(err as Error).message}`);
      setOutputJson('');
      setIsValid(false);
    }
  };

  const validateJson = () => {
    if (!inputJson.trim()) {
      setError('请输入JSON数据');
      setIsValid(null);
      return;
    }

    try {
      JSON.parse(inputJson);
      setError('');
      setIsValid(true);
    } catch (err) {
      setError(`JSON格式错误: ${(err as Error).message}`);
      setIsValid(false);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      alert('已复制到剪贴板');
    } catch (err) {
      console.error('复制失败:', err);
      alert('复制失败，请手动复制');
    }
  };

  const clearAll = () => {
    setInputJson('');
    setOutputJson('');
    setError('');
    setIsValid(null);
  };

  const loadSample = () => {
    const sampleJson = {
      "name": "张三",
      "age": 30,
      "city": "北京",
      "hobbies": ["读书", "游泳", "编程"],
      "address": {
        "street": "中关村大街",
        "number": 123,
        "zipcode": "100080"
      },
      "married": true,
      "children": null
    };
    setInputJson(JSON.stringify(sampleJson));
  };

  return (
    <div className="tool-container">
      <SEO 
        title="JSON格式化工具 - 在线JSON美化压缩验证"
        description="免费在线JSON格式化工具，支持JSON美化、压缩、验证和统计。提供2空格、4空格缩进选项，实时错误检测，一键复制功能。"
        keywords="JSON格式化,JSON美化,JSON压缩,JSON验证,JSON工具,在线JSON编辑器"
      />
      <h1 className="tool-title">JSON格式化工具</h1>
      <p className="tool-description">
        格式化、验证和美化JSON数据，支持压缩和语法检查
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
        <div className="form-group">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <label className="form-label">输入JSON</label>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button 
                className="btn btn-secondary"
                onClick={loadSample}
                style={{ fontSize: '12px', padding: '6px 12px' }}
              >
                加载示例
              </button>
              <button 
                className="btn btn-secondary"
                onClick={clearAll}
                style={{ fontSize: '12px', padding: '6px 12px' }}
              >
                清空
              </button>
            </div>
          </div>
          <textarea
            className="form-textarea"
            value={inputJson}
            onChange={(e) => {
              setInputJson(e.target.value);
              setError('');
              setIsValid(null);
            }}
            placeholder="请输入JSON数据..."
            style={{ height: '300px', fontSize: '14px' }}
          />
        </div>

        <div className="form-group">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <label className="form-label">格式化结果</label>
            {outputJson && (
              <button 
                className="btn btn-secondary"
                onClick={() => copyToClipboard(outputJson)}
                style={{ fontSize: '12px', padding: '6px 12px' }}
              >
                复制结果
              </button>
            )}
          </div>
          <textarea
            className="form-textarea"
            value={outputJson}
            readOnly
            placeholder="格式化后的JSON将显示在这里..."
            style={{ height: '300px', fontSize: '14px', backgroundColor: '#f8f9fa' }}
          />
        </div>
      </div>

      <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', marginBottom: '20px', flexWrap: 'wrap' }}>
        <button 
          className="btn btn-primary"
          onClick={() => formatJson(2)}
        >
          格式化 (2空格)
        </button>
        <button 
          className="btn btn-primary"
          onClick={() => formatJson(4)}
        >
          格式化 (4空格)
        </button>
        <button 
          className="btn btn-primary"
          onClick={minifyJson}
        >
          压缩JSON
        </button>
        <button 
          className="btn btn-secondary"
          onClick={validateJson}
        >
          验证JSON
        </button>
      </div>

      {/* 状态显示 */}
      {isValid !== null && (
        <div style={{
          padding: '15px',
          borderRadius: '8px',
          marginBottom: '20px',
          backgroundColor: isValid ? '#d4edda' : '#f8d7da',
          color: isValid ? '#155724' : '#721c24',
          border: `1px solid ${isValid ? '#c3e6cb' : '#f5c6cb'}`
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '18px' }}>
              {isValid ? '✅' : '❌'}
            </span>
            <span>
              {isValid ? 'JSON格式正确' : 'JSON格式错误'}
            </span>
          </div>
        </div>
      )}

      {error && (
        <div style={{
          backgroundColor: '#f8d7da',
          color: '#721c24',
          padding: '15px',
          borderRadius: '8px',
          marginBottom: '20px',
          border: '1px solid #f5c6cb',
          fontFamily: 'Courier New, monospace',
          fontSize: '14px'
        }}>
          {error}
        </div>
      )}

      {/* JSON信息统计 */}
      {outputJson && (
        <div className="result-container">
          <h3 className="result-title">JSON信息统计</h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '15px'
          }}>
            <div style={{ backgroundColor: 'white', padding: '15px', borderRadius: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#667eea' }}>
                {outputJson.split('\n').length}
              </div>
              <div style={{ color: '#7f8c8d', fontSize: '14px' }}>行数</div>
            </div>
            <div style={{ backgroundColor: 'white', padding: '15px', borderRadius: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#667eea' }}>
                {outputJson.length}
              </div>
              <div style={{ color: '#7f8c8d', fontSize: '14px' }}>字符数</div>
            </div>
            <div style={{ backgroundColor: 'white', padding: '15px', borderRadius: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#667eea' }}>
                {new Blob([outputJson]).size}
              </div>
              <div style={{ color: '#7f8c8d', fontSize: '14px' }}>字节数</div>
            </div>
          </div>
        </div>
      )}

      <div style={{ marginTop: '30px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
        <h3 style={{ color: '#2c3e50', marginBottom: '15px' }}>功能说明</h3>
        <ul style={{ color: '#7f8c8d', lineHeight: '1.6' }}>
          <li><strong>格式化：</strong> 将压缩的JSON数据格式化为易读的形式</li>
          <li><strong>压缩：</strong> 移除所有不必要的空格和换行符</li>
          <li><strong>验证：</strong> 检查JSON语法是否正确</li>
          <li><strong>统计：</strong> 显示JSON的行数、字符数和字节数</li>
          <li><strong>复制：</strong> 一键复制格式化后的结果</li>
        </ul>
      </div>
    </div>
  );
};

export default JsonFormatter;